import { Controller } from "@hotwired/stimulus"

// Handles recurrence form interactions for event creation/editing
export default class extends Controller {
  static targets = [
    "enabledCheckbox",
    "optionsPanel",
    "frequency",
    "interval",
    "intervalLabel",
    "weekdaysPanel",
    "monthlyPanel",
    "monthlyType",
    "monthlyOrdinal",
    "monthlyWeekday",
    "endType",
    "countPanel",
    "untilPanel",
    "count",
    "until",
    "hiddenInput",
    "preview"
  ]

  static values = {
    initialRule: String
  }

  connect() {
    this.updateVisibility()
    this.updateIntervalLabel()
    if (this.initialRuleValue) {
      this.parseInitialRule()
    }
    this.buildRrule()
  }

  parseInitialRule() {
    const parts = this.initialRuleValue.split(";").reduce((acc, part) => {
      const [key, value] = part.split("=")
      acc[key] = value
      return acc
    }, {})

    // Set enabled
    if (this.hasEnabledCheckboxTarget) {
      this.enabledCheckboxTarget.checked = true
    }

    // Set frequency
    if (parts.FREQ && this.hasFrequencyTarget) {
      this.frequencyTarget.value = parts.FREQ
    }

    // Set interval
    if (parts.INTERVAL && this.hasIntervalTarget) {
      this.intervalTarget.value = parts.INTERVAL
    }

    // Set weekdays for WEEKLY
    if (parts.BYDAY && parts.FREQ === "WEEKLY") {
      const days = parts.BYDAY.split(",")
      this.element.querySelectorAll("input[name='recurrence_weekdays[]']").forEach(checkbox => {
        checkbox.checked = days.includes(checkbox.value)
      })
    }

    // Set monthly type and values
    if (parts.BYDAY && parts.FREQ === "MONTHLY") {
      const match = parts.BYDAY.match(/^(-?\d+)(\w{2})$/)
      if (match) {
        if (this.hasMonthlyTypeTarget) {
          this.element.querySelectorAll("input[name='recurrence_monthly_type']").forEach(radio => {
            radio.checked = radio.value === "weekday"
          })
        }
        if (this.hasMonthlyOrdinalTarget) {
          this.monthlyOrdinalTarget.value = match[1]
        }
        if (this.hasMonthlyWeekdayTarget) {
          this.monthlyWeekdayTarget.value = match[2]
        }
        // Also check the corresponding weekday checkbox in "On these days"
        const dayCode = match[2]
        this.element.querySelectorAll("input[name='recurrence_weekdays[]']").forEach(checkbox => {
          checkbox.checked = checkbox.value === dayCode
        })
      }
    }

    // Set end type and values
    if (parts.COUNT) {
      this.element.querySelectorAll("input[name='recurrence_end_type']").forEach(radio => {
        radio.checked = radio.value === "count"
      })
      if (this.hasCountTarget) {
        this.countTarget.value = parts.COUNT
      }
    } else if (parts.UNTIL) {
      this.element.querySelectorAll("input[name='recurrence_end_type']").forEach(radio => {
        radio.checked = radio.value === "until"
      })
      if (this.hasUntilTarget) {
        // Parse YYYYMMDD format to YYYY-MM-DD
        const year = parts.UNTIL.substring(0, 4)
        const month = parts.UNTIL.substring(4, 6)
        const day = parts.UNTIL.substring(6, 8)
        this.untilTarget.value = `${year}-${month}-${day}`
      }
    }

    this.updateVisibility()
    this.updateIntervalLabel()
  }

  toggleEnabled() {
    this.updateVisibility()
    this.buildRrule()
  }

  updateVisibility() {
    const enabled = this.hasEnabledCheckboxTarget && this.enabledCheckboxTarget.checked

    if (this.hasOptionsPanelTarget) {
      this.optionsPanelTarget.classList.toggle("hidden", !enabled)
    }

    const frequency = this.hasFrequencyTarget ? this.frequencyTarget.value : "WEEKLY"

    if (this.hasWeekdaysPanelTarget) {
      this.weekdaysPanelTarget.classList.toggle("hidden", frequency !== "WEEKLY")
    }

    if (this.hasMonthlyPanelTarget) {
      this.monthlyPanelTarget.classList.toggle("hidden", frequency !== "MONTHLY")
    }

    this.updateEndPanels()
  }

  updateEndPanels() {
    const selectedEndType = this.element.querySelector("input[name='recurrence_end_type']:checked")?.value || "never"

    if (this.hasCountPanelTarget) {
      this.countPanelTarget.classList.toggle("hidden", selectedEndType !== "count")
    }

    if (this.hasUntilPanelTarget) {
      this.untilPanelTarget.classList.toggle("hidden", selectedEndType !== "until")
    }
  }

  changeFrequency() {
    this.updateVisibility()
    this.updateIntervalLabel()
    this.buildRrule()
  }

  updateIntervalLabel() {
    if (!this.hasIntervalLabelTarget || !this.hasFrequencyTarget) return

    const frequency = this.frequencyTarget.value
    const labels = {
      DAILY: "day(s)",
      WEEKLY: "week(s)",
      MONTHLY: "month(s)",
      YEARLY: "year(s)"
    }
    this.intervalLabelTarget.textContent = labels[frequency] || "period(s)"
  }

  changeEndType() {
    this.updateEndPanels()
    this.buildRrule()
  }

  buildRrule() {
    if (!this.hasHiddenInputTarget) return

    const enabled = this.hasEnabledCheckboxTarget && this.enabledCheckboxTarget.checked
    if (!enabled) {
      this.hiddenInputTarget.value = ""
      this.updatePreview("")
      return
    }

    const parts = []

    // FREQ
    const frequency = this.hasFrequencyTarget ? this.frequencyTarget.value : "WEEKLY"
    parts.push(`FREQ=${frequency}`)

    // INTERVAL
    const interval = this.hasIntervalTarget ? parseInt(this.intervalTarget.value, 10) : 1
    if (interval > 1) {
      parts.push(`INTERVAL=${interval}`)
    }

    // BYDAY for WEEKLY
    if (frequency === "WEEKLY") {
      const selectedDays = Array.from(
        this.element.querySelectorAll("input[name='recurrence_weekdays[]']:checked")
      ).map(cb => cb.value)

      if (selectedDays.length > 0) {
        parts.push(`BYDAY=${selectedDays.join(",")}`)
      }
    }

    // BYDAY for MONTHLY (ordinal day)
    if (frequency === "MONTHLY") {
      const monthlyType = this.element.querySelector("input[name='recurrence_monthly_type']:checked")?.value
      if (monthlyType === "weekday" && this.hasMonthlyOrdinalTarget && this.hasMonthlyWeekdayTarget) {
        const ordinal = this.monthlyOrdinalTarget.value
        const weekday = this.monthlyWeekdayTarget.value
        if (ordinal && weekday) {
          parts.push(`BYDAY=${ordinal}${weekday}`)
        }
      }
    }

    // End condition
    const endType = this.element.querySelector("input[name='recurrence_end_type']:checked")?.value || "never"

    if (endType === "count" && this.hasCountTarget) {
      const count = parseInt(this.countTarget.value, 10)
      if (count > 0) {
        parts.push(`COUNT=${count}`)
      }
    } else if (endType === "until" && this.hasUntilTarget && this.untilTarget.value) {
      // Convert YYYY-MM-DD to YYYYMMDD
      const untilDate = this.untilTarget.value.replace(/-/g, "")
      parts.push(`UNTIL=${untilDate}`)
    }

    const rrule = parts.join(";")
    this.hiddenInputTarget.value = rrule
    this.updatePreview(rrule)
  }

  updatePreview(rrule) {
    if (!this.hasPreviewTarget) return

    if (!rrule) {
      this.previewTarget.textContent = "Does not repeat"
      return
    }

    // Simple client-side preview (server-side will be more accurate)
    this.previewTarget.textContent = this.humanReadableRrule(rrule)
  }

  humanReadableRrule(rrule) {
    const parts = rrule.split(";").reduce((acc, part) => {
      const [key, value] = part.split("=")
      acc[key] = value
      return acc
    }, {})

    const freq = parts.FREQ
    const interval = parseInt(parts.INTERVAL || "1", 10)

    let result = "Occurs "

    // Frequency
    switch (freq) {
      case "DAILY":
        result += interval === 1 ? "daily" : `every ${interval} days`
        break
      case "WEEKLY":
        result += interval === 1 ? "weekly" : `every ${interval} weeks`
        break
      case "MONTHLY":
        result += interval === 1 ? "monthly" : `every ${interval} months`
        break
      case "YEARLY":
        result += interval === 1 ? "yearly" : `every ${interval} years`
        break
    }

    // BYDAY
    if (parts.BYDAY) {
      const dayNames = {
        SU: "Sunday", MO: "Monday", TU: "Tuesday", WE: "Wednesday",
        TH: "Thursday", FR: "Friday", SA: "Saturday"
      }
      const ordinalNames = {
        "1": "first", "2": "second", "3": "third", "4": "fourth", "5": "fifth", "-1": "last"
      }

      const days = parts.BYDAY.split(",").map(day => {
        const match = day.match(/^(-?\d+)(\w{2})$/)
        if (match) {
          return `the ${ordinalNames[match[1]] || match[1]} ${dayNames[match[2]] || match[2]}`
        }
        return dayNames[day] || day
      })
      result += ` on ${this.formatDayList(days)}`
    }

    // Count
    if (parts.COUNT) {
      const count = parseInt(parts.COUNT, 10)
      result += ` for ${count} ${count === 1 ? "occurrence" : "occurrences"}`
    }

    // Until
    if (parts.UNTIL) {
      const year = parts.UNTIL.substring(0, 4)
      const month = parts.UNTIL.substring(4, 6)
      const day = parts.UNTIL.substring(6, 8)
      const date = new Date(year, month - 1, day)
      result += ` until ${date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`
    }

    return result
  }

  // Format a list of days with proper English grammar (e.g., "Monday, Wednesday, and Friday")
  formatDayList(days) {
    if (days.length === 0) return ""
    if (days.length === 1) return days[0]
    if (days.length === 2) return `${days[0]} and ${days[1]}`
    return `${days.slice(0, -1).join(", ")}, and ${days[days.length - 1]}`
  }
}
